import { useState } from "react";
import { Pencil, Trash2, Check, X } from "lucide-react";
import type { MemoryEntry } from "../../types/orgMemory";

type MemoryEntryCardProps = {
  entry: MemoryEntry;
  isSaving: boolean;
  onSaveAnswer: (answer: string) => void;
  onDelete: () => void;
};

export const MemoryEntryCard = ({ entry, isSaving, onSaveAnswer, onDelete }: MemoryEntryCardProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draftA, setDraftA] = useState(entry.answer);

  const handleSave = () => {
    if (!draftA.trim()) return;
    onSaveAnswer(draftA.trim());
    setIsEditing(false);
  };

  const handleCancel = () => {
    setDraftA(entry.answer);
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <div className="rounded-lg border border-emerald-300 bg-white p-4 shadow-sm space-y-3">
        <div>
          <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Question</label>
          <p className="rounded-md bg-gray-50 px-3 py-2 text-sm font-medium text-gray-700">{entry.question}</p>
        </div>
        <div>
          <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Answer</label>
          <textarea
            autoFocus
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
            rows={5}
            value={draftA}
            onChange={(e) => setDraftA(e.target.value)}
          />
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            <Check className="size-3.5" /> {isSaving ? "Saving…" : "Save changes"}
          </button>
          <button
            onClick={handleCancel}
            className="flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <X className="size-3.5" /> Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="group relative rounded-lg border border-gray-200 bg-white p-4 shadow-sm hover:shadow-md transition-shadow">
      <div className="absolute right-2 top-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button onClick={() => setIsEditing(true)} title="Edit" className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700">
          <Pencil className="size-3.5" />
        </button>
        <button onClick={onDelete} title="Delete" className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600">
          <Trash2 className="size-3.5" />
        </button>
      </div>
      <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">Q</p>
      <p className="mb-3 pr-12 text-sm font-medium text-gray-800">{entry.question}</p>
      <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">A</p>
      <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{entry.answer}</p>
      <p className="mt-3 text-xs text-gray-400">{entry.timestamp}</p>
    </div>
  );
};
